import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Share2 } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { BlogCard } from "@/components/BlogsSection";
import { fetchBlog, fetchBlogs } from "@/lib/api";
import { withTM } from "@/components/TM";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const { data: blog, isLoading, isError } = useQuery({ queryKey: ["blog", slug], queryFn: () => fetchBlog(slug) });
  const { data: all = [] } = useQuery({ queryKey: ["blogs"], queryFn: fetchBlogs });
  const others = all.filter((b) => b.slug !== slug).slice(0, 2);
  const origin = window.location.origin;

  const share = async () => {
    const url = `${origin}/blogs/${slug}`;
    if (navigator.share) return navigator.share({ title: blog.title, url });
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  if (isLoading) return <div className="min-h-screen pt-40 text-center text-[var(--muted)]" data-testid="blog-loading">Loading…</div>;
  if (isError || !blog)
    return (
      <div className="min-h-screen pt-40 text-center" data-testid="blog-not-found">
        <h1 className="font-display text-3xl font-bold">Blog not found</h1>
        <Link to="/blogs" className="btn-ghost mt-6">Back to blogs</Link>
      </div>
    );

  return (
    <article className="pt-28 sm:pt-32 pb-16 sm:pb-28" data-testid="blog-post">
      <SEO
        title={`${blog.title} | TruCitizen`}
        description={blog.excerpt}
        path={`/blogs/${blog.slug}`}
        image={blog.cover_image}
        type="article"
        keywords={blog.keywords?.join(", ")}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: blog.title,
            description: blog.excerpt,
            image: [blog.cover_image],
            datePublished: blog.created_at,
            dateModified: blog.updated_at,
            author: { "@type": "Organization", name: "TruCitizen", url: origin },
            publisher: { "@type": "Organization", name: "CreJik Technologies Private Limited", logo: { "@type": "ImageObject", url: `${origin}/logo.png` } },
            mainEntityOfPage: `${origin}/blogs/${blog.slug}`,
            keywords: blog.keywords?.join(", "),
          },
          blog.faqs?.length && {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: blog.faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: origin },
              { "@type": "ListItem", position: 2, name: "Blogs", item: `${origin}/blogs` },
              { "@type": "ListItem", position: 3, name: blog.title, item: `${origin}/blogs/${blog.slug}` },
            ],
          },
        ].filter(Boolean)}
      />

      <header className="max-w-4xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Link to="/blogs" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="blog-back-link">
            <ArrowLeft size={16} /> All blogs
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-xs font-mono uppercase tracking-widest text-[var(--muted)]">
            {blog.tags?.map((t) => <span key={t} className="px-2.5 py-1 rounded-full bg-white/70 border border-black/5 text-[var(--saffron-line)]">{t}</span>)}
          </div>
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[1.04] text-[var(--ink)]" data-testid="blog-title">
            {blog.title}
          </h1>
          <p className="mt-6 text-base md:text-lg text-[var(--ink-soft)] leading-relaxed max-w-3xl">{blog.excerpt}</p>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-black/[0.08] pt-6">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="TruCitizen" className="w-11 h-11 rounded-full object-contain bg-white p-1 border border-black/5" />
              <div>
                <p className="text-sm font-bold text-[var(--ink)]" data-testid="blog-author">By {blog.author}</p>
                <p className="text-xs text-[var(--muted)]">{fmt(blog.created_at)} · <Clock size={11} className="inline -mt-0.5" /> {blog.read_time}</p>
              </div>
            </div>
            <button onClick={share} className="btn-ghost !py-2 !px-4 text-sm" data-testid="blog-share-button"><Share2 size={14} /> Share</button>
          </div>
        </Reveal>
      </header>

      <motion.figure
        initial={{ opacity: 0, scale: 0.97, clipPath: "inset(6% round 24px)" }}
        animate={{ opacity: 1, scale: 1, clipPath: "inset(0% round 24px)" }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className="max-w-6xl mx-auto px-5 sm:px-8 mt-8 sm:mt-12"
      >
        <img src={blog.cover_image} alt={blog.title} className="w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] object-cover rounded-2xl sm:rounded-3xl shadow-[0_40px_80px_-40px_rgba(15,23,42,0.4)]" data-testid="blog-cover-image" />
      </motion.figure>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-14 prose-tc" data-testid="blog-content" dangerouslySetInnerHTML={{ __html: withTM(blog.content) }} />

      {blog.faqs?.length > 0 && (
        <section className="max-w-3xl mx-auto px-5 sm:px-8 mt-16" data-testid="blog-faqs">
          <p className="eyebrow mb-4">Quick answers</p>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-[var(--ink)]">Frequently asked</h2>
          <dl className="mt-6 divide-y divide-black/[0.08]">
            {blog.faqs.map((f, i) => (
              <div key={i} className="py-5">
                <dt className="font-bold text-[var(--ink)]">{f.question}</dt>
                <dd className="mt-2 text-[var(--ink-soft)] leading-relaxed">{f.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {others.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-28">
          <p className="eyebrow mb-8">Keep reading</p>
          <div className="grid md:grid-cols-2 gap-6">{others.map((b, i) => <BlogCard key={b.id} blog={b} index={i} />)}</div>
        </section>
      )}
    </article>
  );
}
